import { createAsyncThunk, Dispatch } from "@reduxjs/toolkit";
import { has, keys } from "lodash";

import { SubscriptionService } from "Bands/services";
import {
  BAND_ACTIONS,
  IBandSubscriptionParams,
  SubscriptionStatus,
  IEditBandSubscriptionParams,
} from "Bands/types";
import { IStoreState } from "App/types";
import { getSubscriptionsAction } from "../utils/helpers";

export const createBandSubscription = createAsyncThunk(
  BAND_ACTIONS.createBandSubscription,
  async (subscription: IBandSubscriptionParams, { rejectWithValue }) => {
    try {
      const data = await SubscriptionService.createSubscription(subscription);
      const key = keys(data.subscriptionRequest)[0];
      const createdItem = data.subscriptionRequest?.[key];
      return { createdItem };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editBandSubscription = createAsyncThunk(
  BAND_ACTIONS.editBandSubscription,
  async (data: IEditBandSubscriptionParams, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.editSubscription(data);
      const key = keys(response.subscriptionRequest)[0];
      const createdItem = response.subscriptionRequest?.[key];
      return { createdItem };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetSubscriptionsAction = (
  dispatch: Dispatch<any>,
  props: {
    band?: string;
    company?: string;
    status?: SubscriptionStatus;
    page?: number;
    itemsPerPage?: number;
  } = {},
  isRecursive?: boolean
) => {
  props.page = props.page || 1;
  props.itemsPerPage = props.itemsPerPage || 15;

  return dispatch(
    getSubscriptions({
      ...props,
      action: getSubscriptionsAction(props),
      paginationKey: BAND_ACTIONS.getSubscriptions,
      isRecursive,
    })
  );
};

export const getSubscriptions = createAsyncThunk(
  BAND_ACTIONS.getSubscriptions,
  async (
    {
      paginationKey,
      action,
      isRecursive,
      ...props
    }: {
      band?: string;
      company?: string;
      action: string;
      status?: SubscriptionStatus;
      page?: number;
      paginationKey: string;
      itemsPerPage?: number;
      isRecursive?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await SubscriptionService.getSubscriptions(
        props,
        isRecursive
      );

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  // Do not fetch the subscriptions for the band/company are already in the state.
  {
    condition: ({ band, company }, { getState }) => {
      const state = getState() as IStoreState;
      const subscription = state.bandData.data.subscription;

      const action = getSubscriptionsAction({ band, company });

      return !has(subscription, action);
    },
  }
);

export const startTrial = createAsyncThunk(
  BAND_ACTIONS.startTrial,
  async (
    {
      bandId,
      start,
      stop,
    }: {
      bandId: string;
      start: string;
      stop: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await SubscriptionService.startTrial(bandId, start, stop);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editTrial = createAsyncThunk(
  BAND_ACTIONS.editTrial,
  async (
    {
      subscriptionId,
      ...props
    }: {
      subscriptionId: string;
      stop: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await SubscriptionService.editTrial(subscriptionId, props);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editSubscriptionPayment = createAsyncThunk(
  BAND_ACTIONS.editSubscriptionPayment,
  async (
    {
      subscriptionId,
      subscription,
    }: {
      subscriptionId: string;
      subscription: Partial<IBandSubscriptionParams>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await SubscriptionService.changePaymentMethod(
        subscriptionId,
        subscription
      );
      const key = keys(response.subscriptionRequest)[0];
      const createdItem = response.subscriptionRequest?.[key];

      return { createdItem };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  BAND_ACTIONS.cancelBandSubscription,
  async (subscriptionId: string, { rejectWithValue }) => {
    try {
      const data = await SubscriptionService.cancelSubscription(subscriptionId);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
